import { addHours, format } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import useApiCall from '../../../../hooks/useApiCall';
import useGetGeneralAppData from '../../../../hooks/useGetGeneralAppData';
import ConsultsService from '../../../../services/ConsultsService';
import customerRequestsService from '../../../../services/customerRequestsService';
import emailsService from '../../../../services/emailsService';
import removeDuplicates from '../../../../utils/removeDuplicates';
import splitArray from '../../../../utils/splitArray';
import { ActionTypes, DadosConsulum, DownloadDocumentsApiResponse, ListsForSearchingConsultsApiResponse, OptionType, PeriodType, SearchConsultsApiResponse, Value, ValuePiece } from '../types';

export default function useSearchConsult() {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [isSelectingConsults, setIsSelectingConsults] = useState(false);
  const [selectedConsults, setSelectedConsults] = useState<number[]>([]);

  const [selectedType, setSelectedType] = useState<'preAdm' | 'base'>('preAdm');
  const [fullConsults, setFullConsults] = useState<DadosConsulum[]>([]);
  const [consultsSplitted, setConsultsSplitted] = useState<DadosConsulum[][]>([]);
  const [filteredConsults, setFilteredConsults] = useState<DadosConsulum[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [searchLabel, setSearchLabel] = useState<string>('');

  const [workplacesOptions, setWorkplacesOptins] = useState<OptionType[]>([]);
  const [filesOptions, setFilesOptions] = useState<OptionType[]>([]);

  const [actionModalShow, setActionModalShow] = useState<boolean>(false);
  const [selectedAction, setSelectedAction] = useState<ActionTypes>('documents');

  const [searchByCpfModalShow, setSearchByCpfModalShow] = useState<boolean>(false);
  const [searchByCodeModalShow, setSearchByCodeModalShow] = useState<boolean>(false);
  const [searchByNameModalShow, setSearchByNameModalShow] = useState<boolean>(false);
  const [searchByRegistrationModalShow, setSearchByRegistrationModalShow] = useState<boolean>(false);
  const [searchByPeriodModalShow, setSearchByPeriodModalShow] = useState<boolean>(false);
  const [searchByWorkplaceModalShow, setSearchByWorkplaceModalShow] = useState<boolean>(false);
  const [searchByFileModalShow, setSearchByFileModalShow] = useState<boolean>(false);

  const [routingStatus, setRoutingStatus] = useState<OptionType>({ value: '', label: 'Filtrar por status Roteirização' });
  // const [routingStatusOptions, setRoutingStatusOptions] = useState<OptionType[]>([]);
  const [consultStatus, setConsultStatus] = useState<OptionType>({ value: '', label: 'Filtrar por status Consulta' });
  // const [consultStatusOptions, setConsultStatusOptions] = useState<OptionType[]>([]);

  const [emailSendZip, setEmailSendZip] = useState<string>('');
  const [isFileSearch, setIsFileSearch] = useState<boolean>(false);
  const [fileId, setFileId] = useState<number | string>('');
  const [removeFileModalShow, setRemoveFileModalShow] = useState<boolean>(false);
  const [hasOpenedRequest, setHasOpenedRequest] = useState<boolean>(false);
  const [sendEmailsModalShow, setSendEmailsModalShow] = useState<boolean>(false);

  const routingStatusOptions = useMemo(() => {
    const mappedRoutingStatus = fullConsults.map((x) => x.statusRot);
    const uniqueRoutingStatus = removeDuplicates(mappedRoutingStatus);

    return [{ value: '', label: 'Filtrar por status Roteirização'}].concat(uniqueRoutingStatus.map((x) => ({ value: x, label: x })));
  }, [fullConsults]);

  const consultStatusOptions = useMemo(() => {
    const consultStatusOptions = fullConsults.map((x) => x.statusConsulta);
    const uniqueConsultStatus = removeDuplicates(consultStatusOptions);

    return [{ value: '', label: 'Filtrar por status Consulta'}].concat(uniqueConsultStatus.map((x) => ({ value: x, label: x })));
  }, [fullConsults]);

  const { userProfile, companyCode, userCode, userName, companyName } = useGetGeneralAppData();
  const { apiCall } = useApiCall();

  const pagesQuantity = useMemo(() => consultsSplitted.length, [consultsSplitted.length]);

  function handlePageChange(page: number) {
    setCurrentPage(page);
    setFilteredConsults(consultsSplitted[page]);
  }

  function handleConsultSelection(x: number) {
    if (selectedConsults.includes(x)) {
      setSelectedConsults((prev) => prev.filter((y) => y !== x));
    } else {
      setSelectedConsults((prev) => [...prev, x]);
    }
  }

  function clearAllSelectedConsults() {
    setSelectedConsults([]);
  }

  function addAllSelectedConsults() {
    const allFilteredConsults = consultsSplitted.flat().map((x) => x.codConsulta);
    setSelectedConsults(allFilteredConsults);
  }

  function handleEmailSendZipChange(e: React.ChangeEvent<HTMLInputElement>) {
    setEmailSendZip(e.target.value);
  }

  const filterConsults = useCallback(() => {
    const filteredConsults = fullConsults.filter((cst) => {
      const routingStatusFilter = routingStatus.value === '' || (cst.statusRot === routingStatus.value);
      const consultStatusFilter = consultStatus.value === '' || (cst.statusConsulta === consultStatus.value);

      return consultStatusFilter && routingStatusFilter;
    });

    const consultsSplittedByFilters = splitArray(filteredConsults);
    setConsultsSplitted(consultsSplittedByFilters);
    setFilteredConsults(consultsSplittedByFilters[0]);
  }, [consultStatus.value, fullConsults, routingStatus.value]);

  useEffect(() => {
    filterConsults();
  }, [filterConsults]);

  const searchByCpf = useCallback(async (cpf: string) => {
    await apiCall({
      apiToCall: ConsultsService.searchConsults,
      queryParams: {
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: companyCode,
        tipoPesquisa: 2,
        tipoRot: 1,
        valorLong: 0,
        valorInt: 0,
        valorInt1: 0,
        valorString: cpf,
        processo: selectedType === 'preAdm' ? 18 : 79,
        data1: new Date().toISOString().split('T')[0],
        data2: new Date().toISOString().split('T')[0],
      },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      catchMessage: 'Não foi possível realizar a pesquisa por CPF',
      actionAfterResponse: (response: SearchConsultsApiResponse) => {
        if (!response.codigo) {
          toast.error(`Não foi possível realizar a pesquisa por CPF (${response.msg})`);
          return;
        }

        clearAllSelectedConsults();
        setRoutingStatus({ value: '', label: 'Filtrar por status Roteirização' });
        setConsultStatus({ value: '', label: 'Filtrar por status Consulta' });
        setIsSelectingConsults(false);
        if (!response.dadosConsulta || response.dadosConsulta.length === 0) {
          toast.warn('Nenhuma consulta encontrada conforme CPF informado');
          setFullConsults([]);
          return;
        }

        setIsFileSearch(false);
        setFullConsults(response.dadosConsulta);
        const splitedArray = splitArray(response.dadosConsulta);
        setConsultsSplitted(splitedArray);
        setFilteredConsults(splitedArray[0]);
        setCurrentPage(0);
        setSearchLabel(`Pesquisa por CPF - ${cpf}`);
        setSearchByCpfModalShow(false);
      },
    });
  }, [apiCall, companyCode, selectedType, userCode, userProfile]);

  const searchByCode = useCallback(async (code: string | number) => {
    await apiCall({
      apiToCall: ConsultsService.searchConsults,
      queryParams: {
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: companyCode,
        tipoPesquisa: 1,
        tipoRot: 1,
        valorLong: code,
        valorInt: 0,
        valorInt1: 0,
        valorString: 0,
        processo: selectedType === 'preAdm' ? 18 : 79,
        data1: new Date().toISOString().split('T')[0],
        data2: new Date().toISOString().split('T')[0],
      },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      catchMessage: 'Não foi possível realizar a pesquisa por Código da Consulta',
      actionAfterResponse: (response: SearchConsultsApiResponse) => {
        if (!response.codigo) {
          toast.error(`Não foi possível realizar a pesquisa por Código da Consulta', (${response.msg})`);
          return;
        }

        clearAllSelectedConsults();
        setRoutingStatus({ value: '', label: 'Filtrar por status Roteirização' });
        setConsultStatus({ value: '', label: 'Filtrar por status Consulta' });
        setIsSelectingConsults(false);
        if (!response.dadosConsulta || response.dadosConsulta.length === 0) {
          toast.warn('Nenhuma consulta encontrada conforme Código da Consulta informado');
          setFullConsults([]);
          return;
        }

        setIsFileSearch(false);
        setFullConsults(response.dadosConsulta);
        const splitedArray = splitArray(response.dadosConsulta);
        setConsultsSplitted(splitedArray);
        setFilteredConsults(splitedArray[0]);
        setCurrentPage(0);
        setSearchLabel(`Pesquisa por Código da Consulta - ${code}`);
        setSearchByCodeModalShow(false);
      },
    });
  }, [apiCall, companyCode, selectedType, userCode, userProfile]);

  const searchByName = useCallback(async (userName: string) => {
    await apiCall({
      apiToCall: ConsultsService.searchConsults,
      queryParams: {
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: companyCode,
        tipoPesquisa: 3,
        tipoRot: 1,
        valorLong: 0,
        valorInt: 0,
        valorInt1: 0,
        valorString: userName,
        processo: selectedType === 'preAdm' ? 18 : 79,
        data1: new Date().toISOString().split('T')[0],
        data2: new Date().toISOString().split('T')[0],
      },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      catchMessage: 'Não foi possível realizar a pesquisa por Nome',
      actionAfterResponse: (response: SearchConsultsApiResponse) => {
        if (!response.codigo) {
          toast.error(`Não foi possível realizar a pesquisa por Nome', (${response.msg})`);
          return;
        }

        clearAllSelectedConsults();
        setRoutingStatus({ value: '', label: 'Filtrar por status Roteirização' });
        setConsultStatus({ value: '', label: 'Filtrar por status Consulta' });
        setIsSelectingConsults(false);
        if (!response.dadosConsulta || response.dadosConsulta.length === 0) {
          toast.warn('Nenhuma consulta encontrada conforme Nome informado');
          setFullConsults([]);
          return;
        }

        setIsFileSearch(false);
        setFullConsults(response.dadosConsulta);
        const splitedArray = splitArray(response.dadosConsulta);
        setConsultsSplitted(splitedArray);
        setFilteredConsults(splitedArray[0]);
        setCurrentPage(0);
        setSearchLabel(`Pesquisa por Nome - ${userName}`);
        setSearchByNameModalShow(false);
      },
    });
  }, [apiCall, companyCode, selectedType, userCode, userProfile]);

  const searchByRegistration = useCallback(async (registration: string) => {
    await apiCall({
      apiToCall: ConsultsService.searchConsults,
      queryParams: {
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: companyCode,
        tipoPesquisa: 4,
        tipoRot: 1,
        valorLong: 0,
        valorInt: 0,
        valorInt1: 0,
        valorString: registration,
        processo: selectedType === 'preAdm' ? 18 : 79,
        data1: new Date().toISOString().split('T')[0],
        data2: new Date().toISOString().split('T')[0],
      },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      catchMessage: 'Não foi possível realizar a pesquisa por Matrícula',
      actionAfterResponse: (response: SearchConsultsApiResponse) => {
        if (!response.codigo) {
          toast.error(`Não foi possível realizar a pesquisa por Matrícula', (${response.msg})`);
          return;
        }

        clearAllSelectedConsults();
        setRoutingStatus({ value: '', label: 'Filtrar por status Roteirização' });
        setConsultStatus({ value: '', label: 'Filtrar por status Consulta' });
        setIsSelectingConsults(false);
        if (!response.dadosConsulta || response.dadosConsulta.length === 0) {
          toast.warn('Nenhuma consulta encontrada conforme Matrícula informada');
          setFullConsults([]);
          return;
        }

        setIsFileSearch(false);
        setFullConsults(response.dadosConsulta);
        const splitedArray = splitArray(response.dadosConsulta);
        setConsultsSplitted(splitedArray);
        setFilteredConsults(splitedArray[0]);
        setCurrentPage(0);
        setSearchLabel(`Pesquisa por Matrícula - ${registration}`);
        setSearchByRegistrationModalShow(false);
      },
    });
  }, [apiCall, companyCode, selectedType, userCode, userProfile]);

  function getFirstAndLastPeriod(value: Value): { first: ValuePiece, last: ValuePiece } {
    if (value instanceof Array) {
      // Se for um Range, acessa o primeiro e último elemento
      const [first, last] = value;
      return { first, last };
    } else {
      // Se for um único valor, tanto o primeiro quanto o último são o próprio valor
      return { first: value, last: value };
    }
  }

  const searchByPeriod = useCallback(async (period: Value) => {
    const { first, last } = getFirstAndLastPeriod(period);

    await apiCall({
      apiToCall: ConsultsService.searchConsults,
      queryParams: {
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: companyCode,
        tipoPesquisa: 11,
        tipoRot: 1,
        valorLong: 0,
        valorInt: 0,
        valorInt1: 0,
        valorString: 0,
        data1: first?.toISOString()?.split('T')[0],
        data2: last?.toISOString()?.split('T')[0],
        processo: selectedType === 'preAdm' ? 18 : 79,
      },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      catchMessage: 'Não foi possível realizar a pesquisa por Período',
      actionAfterResponse: (response: SearchConsultsApiResponse) => {
        if (!response.codigo) {
          toast.error(`Não foi possível realizar a pesquisa por Período', (${response.msg})`);
          return;
        }

        clearAllSelectedConsults();
        setRoutingStatus({ value: '', label: 'Filtrar por status Roteirização' });
        setConsultStatus({ value: '', label: 'Filtrar por status Consulta' });
        setIsSelectingConsults(false);
        if (!response.dadosConsulta || response.dadosConsulta.length === 0) {
          toast.warn('Nenhuma consulta encontrada conforme Período informado');
          setFullConsults([]);
          return;
        }

        setIsFileSearch(false);
        setFullConsults(response.dadosConsulta);
        const splitedArray = splitArray(response.dadosConsulta);
        setConsultsSplitted(splitedArray);
        setFilteredConsults(splitedArray[0]);
        setCurrentPage(0);
        console.log(first, last);
        setSearchLabel(`Pesquisa por Período - De ${format(addHours(first!, 3), 'dd/MM/yyyy')} até ${format(addHours(last!, 3), 'dd/MM/yyyy')}`);
        setSearchByPeriodModalShow(false);
      },
    });
  }, [apiCall, companyCode, selectedType, userCode, userProfile]);

  const searchByWorkplace = useCallback(async (period: PeriodType, workplace: OptionType) => {
    await apiCall({
      apiToCall: ConsultsService.searchConsults,
      queryParams: {
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: companyCode,
        tipoPesquisa: 7,
        tipoRot: 1,
        valorLong: workplace.value,
        valorInt: period.month,
        valorInt1: period.year,
        valorString: 0,
        processo: selectedType === 'preAdm' ? 18 : 79,
        data1: new Date().toISOString().split('T')[0],
        data2: new Date().toISOString().split('T')[0],
      },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      catchMessage: 'Não foi possível realizar a pesquisa por Local de Trabalho',
      actionAfterResponse: (response: SearchConsultsApiResponse) => {
        if (!response.codigo) {
          toast.error(`Não foi possível realizar a pesquisa por Local de Trabalho', (${response.msg})`);
          return;
        }

        clearAllSelectedConsults();
        setRoutingStatus({ value: '', label: 'Filtrar por status Roteirização' });
        setConsultStatus({ value: '', label: 'Filtrar por status Consulta' });
        setIsSelectingConsults(false);
        if (!response.dadosConsulta || response.dadosConsulta.length === 0) {
          toast.warn('Nenhuma consulta encontrada conforme Local de Trabalho informado');
          setFullConsults([]);
          return;
        }
        setIsFileSearch(false);

        setFullConsults(response.dadosConsulta);
        const splitedArray = splitArray(response.dadosConsulta);
        setConsultsSplitted(splitedArray);
        setFilteredConsults(splitedArray[0]);
        setCurrentPage(0);
        setSearchLabel(`Pesquisa por Local de Trabalho - ${period.month}/${period.year} - ${workplace.label}`);
        setSearchByWorkplaceModalShow(false);
      },
    });
  }, [apiCall, companyCode, selectedType, userCode, userProfile]);

  const searchByFile = useCallback(async (file: OptionType) => {
    await apiCall({
      apiToCall: ConsultsService.searchConsults,
      queryParams: {
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: companyCode,
        tipoPesquisa: selectedType === 'preAdm' ? (file.type === 1 ? 8 : 8) : 10,
        tipoRot: 1,
        valorLong: selectedType === 'base' ? file.value : 0,
        valorInt: selectedType === 'preAdm' ? file.value : 0,
        valorInt1: 0,
        valorString: 0,
        processo: selectedType === 'preAdm' ? 18 : 79,
        data1: new Date().toISOString().split('T')[0],
        data2: new Date().toISOString().split('T')[0],
      },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      catchMessage: 'Não foi possível realizar a pesquisa por Arquivo',
      actionAfterResponse: (response: SearchConsultsApiResponse) => {
        if (!response.codigo) {
          toast.error(`Não foi possível realizar a pesquisa por Arquivo', (${response.msg})`);
          return;
        }

        clearAllSelectedConsults();
        setRoutingStatus({ value: '', label: 'Filtrar por status Roteirização' });
        setConsultStatus({ value: '', label: 'Filtrar por status Consulta' });
        setIsSelectingConsults(false);
        if (!response.dadosConsulta || response.dadosConsulta.length === 0) {
          toast.warn('Nenhuma consulta encontrada conforme Arquivo informado ou processamento ainda está em andamento');
          setFullConsults([]);
          setConsultsSplitted([]);
          setFilteredConsults([]);
          setCurrentPage(0);
          setSearchLabel(`Pesquisa por Arquivo - ${file.label}`);
          setSearchByFileModalShow(false);
          setIsFileSearch(true);
          setFileId(file.value);
          return;
        }

        setIsFileSearch(true);
        setFileId(file.value);
        setFullConsults(response.dadosConsulta);
        const splitedArray = splitArray(response.dadosConsulta);
        setConsultsSplitted(splitedArray);
        setFilteredConsults(splitedArray[0]);
        setCurrentPage(0);
        setSearchLabel(`Pesquisa por Arquivo - ${file.label}`);
        setSearchByFileModalShow(false);
      },
    });

    await apiCall({
      apiToCall: customerRequestsService.getCustomerRequestsData,
      queryParams: {
        requestType: 'removeFile',
        key: file.value,
        companyCode,
        token: process.env.REACT_APP_NEST_API_TOKEN,
      },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: (response) => {
        if (!response.success || !response.data) {
          setHasOpenedRequest(false);
          return;
        }
        setHasOpenedRequest(true);
      },
      catchAction: () => {
        setHasOpenedRequest(false);
      },
    });
  }, [apiCall, companyCode, selectedType, userCode, userProfile]);

  const loadListsForSearching = useCallback(async (month?: number, year?: number) => {
    await apiCall({
      apiToCall: ConsultsService.loadListsForSearchingConsults,
      queryParams: {
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: companyCode,
        mes: month || (new Date()).getMonth() + 1,
        ano: year || (new Date()).getFullYear(),
      },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: (response: ListsForSearchingConsultsApiResponse) => {
        if (!response.codigo) {
          toast.error(`Não foi possível carregar as listas para pesquisa de consultas (${response.msg})`);
          return;
        }

        const { listaLocaisTrab, listaArqsMes, listaArqaBaseMes } = response;

        if (!listaLocaisTrab || listaLocaisTrab.length === 0) {
          setWorkplacesOptins([]);
        } else {
          setWorkplacesOptins(listaLocaisTrab.map((x) => ({
            value: x.codEnd,
            label: x.denominacaoLT,
          })));
        }

        if (selectedType === 'preAdm') {
          if (!listaArqsMes || listaArqsMes.length === 0) {
            setFilesOptions([]);
          } else {
            setFilesOptions(listaArqsMes.map((x) => ({
              value: x.codArq,
              label: `${x.denominacaoArq} | ${format(new Date(x.dataArq), 'dd/MM/yyyy')} | ${x.statusProc} (${x.totProcessados} de ${x.totRoteirizacoes})`,
              type: x.tipoArq,
              isFinished: x.processoFinalizado,
            })));
          }
        } else {
          if (!listaArqaBaseMes || listaArqaBaseMes.length === 0) {
            setFilesOptions([]);
          } else {
            setFilesOptions(listaArqaBaseMes.map((x) => ({
              value: x.codArq,
              label: `${x.denominacaoArq} | ${format(new Date(x.dataArq), 'dd/MM/yyyy')} | ${x.statusProc} (${x.totProcessados} de ${x.totRoteirizacoes})`,
            })));
          }
        }
      }
    }
    );
  }, [apiCall, companyCode, selectedType, userCode, userProfile]);

  const downloadDocuments = useCallback(async () => {
    const hasSomeMrConsultSelected = selectedConsults.some((x) => fullConsults.find((y) => y.codConsulta === x)?.tipoRotas === 'MULTI ROTAS');

    if (hasSomeMrConsultSelected) {
      toast.warn('Não é possível realizar o download em PDF único se houver uma ou mais consultas do tipo Multi Rotas selecionadas');
      return;
    }

    await apiCall({
      apiToCall: ConsultsService.downloadManyDocuments,
      reqBody: JSON.stringify({
        codEmpresa: companyCode,
        codUsu: userCode,
        perfilUsu: userProfile,
        usuario: userName,
        tipoCarta: 1,
        consultas: selectedConsults.map((x) => ({ codConsulta: x }))
      }),
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: (response) => {
        if (!response.codigo) {
          toast.error(`Não foi possível fazer o download das cartas. Por favor, tente novamente (${response.msg})`);
          return;
        }

        const link = document.createElement('a');
        link.href = response.linkCarta;
        link.setAttribute('download', `Cartas_Selecionadas_Emp_${companyCode}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.info('Seu download foi iniciado (Verifique na parte de downloads em seu navegador');
        setActionModalShow(false);
      }
    });
  }, [apiCall, companyCode, fullConsults, selectedConsults, userCode, userName, userProfile]);

  const downloadDocumentsZip = useCallback(async () => {
    await apiCall({
      apiToCall: ConsultsService.downloadManyDocuments,
      reqBody: JSON.stringify({
        codEmpresa: companyCode,
        codUsu: userCode,
        perfilUsu: userProfile,
        usuario: userName,
        tipoCarta: 2,
        consultas: selectedConsults.map((x) => ({ codConsulta: x }))
      }),
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: async (response: DownloadDocumentsApiResponse) => {
        if (!response.codigo) {
          toast.error(`Não foi possível fazer o download das cartas. Por favor, tente novamente (${response.msg})`);
          return;
        }

        const bodyToSendToZipApi = response.consults;
        const options = {
          method: 'POST',
          headers: { 'content-type': 'application/json', 'Authorization': `Bearer ${process.env.REACT_APP_NEST_API_TOKEN}` },
          body: JSON.stringify({ documents: bodyToSendToZipApi, email: emailSendZip }),
        };
        const res = await fetch(`${process.env.REACT_APP_NEST_API}/documents-zip`, options);
        const resJson = await res.json();
        if (!resJson.success) {
          toast.error(`Não foi possível fazer o download das cartas. Por favor, tente novamente (${resJson.message})`);
          return;
        }

        toast.info('A geração do seu arquivo foi iniciada. Quando o mesmo for finalizado, você receberá no e-mail informado um link para download do mesmo.');
        setActionModalShow(false);
      }
    });
  }, [apiCall, companyCode, emailSendZip, selectedConsults, userCode, userName, userProfile]);

  const confirmAction = useCallback(() => {
    const actionLiterals: { [key: string]: () => Promise<void> } = {
      documents: () => downloadDocuments(),
      documentsZip: () => downloadDocumentsZip(),
    };

    actionLiterals[selectedAction]();
  }, [downloadDocuments, downloadDocumentsZip, selectedAction]);

  const resetPage = useCallback(() => {
    setFullConsults([]);
    setConsultsSplitted([]);
    setFilteredConsults([]);
    setCurrentPage(0);
    setSearchLabel('');
    clearAllSelectedConsults();
    setIsSelectingConsults(false);
    setIsFileSearch(false);
    setFileId('');
    if (companyCode) {
      loadListsForSearching();
    }
  }, [companyCode, loadListsForSearching]);

  const removeFile = useCallback(async () => {
    const isFileFinished = filesOptions.find((x) => x.value === fileId)?.isFinished;

    if (isFileFinished) {
      toast.warn('É permitido remover apenas arquivos que ainda não foram fechados!');
      return;
    }

    let mustSendRequestToRemoveFile = false;
    await apiCall({
      apiToCall: ConsultsService.removeFilesOrSendForRouting,
      reqBody: JSON.stringify({
        codUsu: userCode,
        perfilUsu: userProfile,
        usuario: userName,
        codEmpresa: companyCode,
        tipoProc: selectedType === 'preAdm' ? 1 : 2,
        codArq: selectedType === 'preAdm' ? fileId : 0,
        codArqB: selectedType === 'base' ? fileId : 0,
        consultas: [{ codConsulta: 0 }]
      }),
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      catchMessage: 'Não foi possível remover o arquivo. Por favor, tente novamente',
      actionAfterResponse: (response: { codigo: number, msg: string, excluiuArq: boolean }) => {
        if (!response.codigo) {
          toast.error(`Não foi possível remover o arquivo. Por favor, tente novamente - Erro: ${response.msg}`);
          return;
        }

        if (!response.excluiuArq) {
          mustSendRequestToRemoveFile = true;
          return;
        }

        mustSendRequestToRemoveFile = false;
        toast.success('Arquivo removido com sucesso!');
        setActionModalShow(false);
        resetPage();
        setRemoveFileModalShow(false);
      }
    });

    if (!mustSendRequestToRemoveFile) return;

    await apiCall({
      apiToCall: customerRequestsService.createCustomerRequest,
      reqBody: JSON.stringify({
        requestType: 'removeFile',
        companyCode: String(companyCode),
        title: 'Remoção de arquivo',
        key: String(fileId),
        description: `Remoção do arquivo ${fileId} (${filesOptions.find((x) => x.value === fileId)?.label}), de ${selectedType}, da empresa ${companyCode} (${companyName})`,
        requestBody: {
          codUsu: 211,
          perfilUsu: 1,
          usuario: 'WS Capta Geral',
          codEmpresa: companyCode,
          tipoProc: selectedType === 'preAdm' ? 1 : 2,
          codArq: selectedType === 'preAdm' ? fileId : 0,
          codArqB: selectedType === 'base' ? fileId : 0,
          consultas: [{ codConsulta: 0 }]
        },
        requestUrl: `${process.env.REACT_APP_PATHNAME}/api/SMob_AuditoriaProc`,
        requestMethod: 'POST',
      }),
      queryParams: {
        token: process.env.REACT_APP_NEST_API_TOKEN,
      },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse:  (response) => {
        resetPage();
        setActionModalShow(false);

        if (!response.success) {
          toast.error('O arquivo não pode ser excluído diretamente pois já foi processado, e ocorreu um erro ao enviar a solicitação de exclusão do mesmo ao time interno Capta');
          return;
        }

        toast.info('O arquivo não pode ser excluído diretamente. Foi enviada uma solicitação para que o time interno da Capta realiza sua remoção. Caso o botão de remover esteja habilitado, significa que a remoção não foi aceita pelo time interno. Caso contrário, você apenas não encontrará mais o arquivo na lista.', { autoClose: false, toastId: 'removeFile' });
        setRemoveFileModalShow(false);
      },
      catchMessage: 'O arquivo não pode ser excluído diretamente pois já foi processado, e ocorreu um erro ao enviar a solicitação de exclusão do mesmo ao time interno Capta',
    });
    setRemoveFileModalShow(false);
  }, [apiCall, companyCode, companyName, fileId, filesOptions, resetPage, selectedType, userCode, userName, userProfile]);

  const sendEmails = useCallback(async () => {
    const emailsAddressesToSend = [] as { email: string, nomeUsuario: string}[];
    let totalEmailsSent = 0;
    await apiCall({
      apiToCall: ConsultsService.sendEmailsForAllUsersFromFile,
      reqBody: JSON.stringify({
        codUsu: userCode,
        perfilUsu: userProfile,
        usuario: userName,
        codEmpresa: companyCode,
        tipoArq: selectedType === 'preAdm' ? 1 : 2,
        codArqPreAdm: selectedType === 'preAdm' ? fileId : 0,
        codArqBase: selectedType === 'base' ? fileId : 0,
      }),
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      catchMessage: 'Não foi possível enviar os e-mails. Por favor, tente novamente',
      actionAfterResponse: (response: { codigo: number, msg: string, codRetorno: number, listaEmails: { email: string, nomeUsuario: string}[], totalEnvios: number }) => {
        if (!response.codigo) {
          toast.error(`Não foi possível enviar os e-mails. Por favor, tente novamente - Erro: ${response.msg}`);
          return;
        }

        if (!response.codRetorno) {
          toast.warn(response.msg);
          return;
        }

        emailsAddressesToSend.push(...response.listaEmails);
        totalEmailsSent = response.totalEnvios;
        toast.success('Os e-mails iniciarão a serem enviados!');
        setSendEmailsModalShow(false);
      }
    });

    if (!emailsAddressesToSend.length) return;

    await apiCall({
      apiToCall: emailsService.sendEmails,
      reqBody: JSON.stringify(emailsAddressesToSend.map((email) => ({
        to: email.email,
        salutation: `Prezado(a) ${email.nomeUsuario},`,
        header: 'A solicitação  de envio de e-mails para as consultas do arquivo foi recebida com sucesso!',
        message: `Nós enviaremos os e-mails com a notificação do resultado da roteirização para todos os funcionários que possuem consultas dentro do arquivo selecinoado para este envio. Enviaremos emails para um total de ${totalEmailsSent} usuários.`,
        subject: 'CAPTA - Confirmação de solicitação de envio de e-mails para o arquivo',
      }))),
      queryParams: {
        token: process.env.REACT_APP_NEST_API_TOKEN,
      },
      actionAfterResponse:  (response) => {
        console.log(response);
      },
    });
  }, [apiCall, companyCode, fileId, selectedType, userCode, userName, userProfile]);

  useEffect(() => {
    setFullConsults([]);
    setConsultsSplitted([]);
    setFilteredConsults([]);
    setCurrentPage(0);
    setSearchLabel('');
    clearAllSelectedConsults();
    setIsSelectingConsults(false);
    setIsFileSearch(false);
    setFileId('');
  }, [companyCode, selectedType]);

  useEffect(() => {
    if (companyCode) {
      loadListsForSearching();
    }

    return () => {
      toast.dismiss('removeFile');
    };
  }, [loadListsForSearching, companyCode]);

  return {
    userProfile,
    isLoading,
    companyCode,
    searchByCpfModalShow,
    setSearchByCpfModalShow,
    searchByCpf,
    fullConsults,
    consultsSplitted,
    filteredConsults,
    currentPage,
    setCurrentPage,
    pagesQuantity,
    handlePageChange,
    searchLabel,
    searchByCodeModalShow,
    setSearchByCodeModalShow,
    searchByCode,
    searchByNameModalShow,
    setSearchByNameModalShow,
    searchByName,
    searchByRegistrationModalShow,
    setSearchByRegistrationModalShow,
    searchByRegistration,
    searchByPeriodModalShow,
    setSearchByPeriodModalShow,
    searchByPeriod,
    workplacesOptions,
    filesOptions,
    loadListsForSearching,
    searchByWorkplaceModalShow,
    setSearchByWorkplaceModalShow,
    searchByWorkplace,
    searchByFile,
    searchByFileModalShow,
    setSearchByFileModalShow,
    selectedType,
    setSelectedType,
    isSelectingConsults,
    setIsSelectingConsults,
    selectedConsults,
    handleConsultSelection,
    clearAllSelectedConsults,
    addAllSelectedConsults,
    actionModalShow,
    setActionModalShow,
    selectedAction,
    setSelectedAction,
    confirmAction,
    routingStatus,
    routingStatusOptions,
    setRoutingStatus,
    consultStatus,
    consultStatusOptions,
    setConsultStatus,
    emailSendZip,
    handleEmailSendZipChange,
    isFileSearch,
    removeFileModalShow,
    setRemoveFileModalShow,
    removeFile,
    hasOpenedRequest,
    setSendEmailsModalShow,
    sendEmailsModalShow,
    sendEmails,
  };
}
